/**
 * AppStoreBadges
 * --------------
 * Pair of native-app download badges (App Store + Google Play).  Rendered
 * inline as `<a>` links so the browser handles them as ordinary
 * navigation — no client-side router involvement, no analytics ceremony.
 *
 * Visual style: glass dark chip with the platform glyph + a tiny two-line
 * caption.  Matches the existing ZenScroll dark palette (`#101F1D` base,
 * `#FFD166` accent) and looks balanced next to the hero CTAs.
 *
 * Pass `align` to control flex alignment on the wrapping row (default
 * `'start'`, which means left-align on lg+ and centre on mobile — same as
 * the hero CTA row above it).
 */
import { Apple, Smartphone } from 'lucide-react';

// Capacitor sets one of these on the global before our JS boots, so this
// check works synchronously without importing anything from the plugin.
// Mirrors the helper used in `LandingShell.js`.
const IS_NATIVE_APP = typeof window !== 'undefined'
  && ((window.Capacitor && window.Capacitor.isNativePlatform && window.Capacitor.isNativePlatform())
      || /android|iphone|ipad|ipod/i.test(navigator.userAgent || '') && window.matchMedia('(display-mode: standalone)').matches);

const APP_STORE_URL =
  'https://apps.apple.com/us/app/zenscroll/id6749971356';
const PLAY_STORE_URL =
  'https://play.google.com/store/apps/details?id=com.zenscroll.mobile';

function Badge({ href, eyebrow, primary, Icon, testId }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={testId}
      // `w-full` + the grid wrapper below guarantees both badges occupy
      // identical pixel widths regardless of caption length ("App Store"
      // vs "Google Play").  `min-w-[170px]` is the floor so the badges
      // still read comfortably when alone in a narrow column.
      className="group inline-flex w-full min-w-[170px] items-center gap-3 rounded-2xl border border-white/15 bg-[#101F1D]/80 backdrop-blur-md pl-3 pr-5 py-2.5 hover:bg-[#162039] hover:border-white/25 transition-colors shadow-[0_8px_24px_rgba(0,0,0,0.35)]"
    >
      <Icon className="w-7 h-7 text-white flex-shrink-0" aria-hidden="true" />
      <span className="flex flex-col leading-tight text-left">
        <span className="text-[10px] uppercase tracking-[0.18em] text-white/65">
          {eyebrow}
        </span>
        <span className="text-sm font-semibold text-white">{primary}</span>
      </span>
    </a>
  );
}

export default function AppStoreBadges({ align = 'start', testId = 'app-store-badges' }) {
  // Inside the native iOS/Android wrapper the user is already in the app —
  // "Download on the App Store" is a confusing dead-end.  Hide the entire
  // row on native so the web/marketing surface keeps it but the in-app
  // home page doesn't.
  if (IS_NATIVE_APP) return null;
  const justify =
    align === 'center'
      ? 'justify-center'
      : align === 'end'
      ? 'justify-end'
      : 'justify-center lg:justify-start';
  return (
    <div
      data-testid={testId}
      // 2-column grid means both badges are equal-width by definition.
      // `max-w-[420px]` keeps the pair from stretching across the full
      // hero column on desktop; on mobile the pair fills the viewport
      // gracefully because the parent already centres us.
      className={`grid grid-cols-2 gap-3 max-w-[420px] ${justify === 'justify-center' ? 'mx-auto' : justify === 'justify-end' ? 'ml-auto' : 'mx-auto lg:mx-0'}`}
      aria-label="Download ZenScroll mobile app"
    >
      <Badge
        href={APP_STORE_URL}
        eyebrow="Download on the"
        primary="App Store"
        Icon={Apple}
        testId="app-store-badge-ios"
      />
      <Badge
        href={PLAY_STORE_URL}
        eyebrow="Get it on"
        primary="Google Play"
        Icon={Smartphone}
        testId="app-store-badge-android"
      />
    </div>
  );
}

// Re-export constants so other surfaces (e.g. footer, settings → about)
// can link to the same store URLs without re-hardcoding them.
export { APP_STORE_URL, PLAY_STORE_URL };
